import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';

// ==============================================================
// MODAL SEGURO PARA ALERTAS
// ==============================================================
function SafePortalModal({ children }) {
  const elRef = useRef(null);
  if (!elRef.current && typeof document !== "undefined") {
    const div = document.createElement("div");
    elRef.current = div;
  }
  useEffect(() => {
    const el = elRef.current;
    document.body.appendChild(el);
    return () => { if (el.parentNode) el.parentNode.removeChild(el); };
  }, []);
  if (!elRef.current) return null;
  return ReactDOM.createPortal(children, elRef.current);
}

// ==============================================================
// CONSTANTES
// ==============================================================
const TURNOS = ["Mañana (8:00 AM)", "Tarde (3:00 PM)", "Noche (8:00 PM)"];

// ==============================================================
// COMPONENTE AUXILIAR FUERA DEL PRINCIPAL (EVITA QUE LA PANTALLA SUBA)
// ==============================================================
const TarjetaEquipo = ({ id, titulo, icono, rangoTexto, valor, onChange, evaluarTemp }) => {
  const estado = evaluarTemp(id, valor);
  
  return (
    <div className={`p-4 rounded-2xl border transition-all shadow-sm ${estado === true ? 'bg-emerald-50/80 border-emerald-200' : estado === false ? 'bg-rose-50/80 border-rose-300' : 'bg-white/60 border-slate-200'}`}>
      <div className="flex justify-between items-start mb-3">
        <div className="flex gap-2 items-center">
          <span className="text-2xl">{icono}</span>
          <div>
            <h4 className="text-xs font-black text-slate-800 uppercase leading-tight">{titulo}</h4>
            <p className="text-[9px] font-bold text-slate-500 mt-0.5">{rangoTexto}</p>
          </div>
        </div>
      </div>
      
      <div className="relative">
        <input 
          type="number" 
          step="0.1" 
          name={id} 
          value={valor} 
          onChange={onChange} 
          placeholder=""
          className={`w-full h-[40px] rounded-xl px-4 text-sm font-black shadow-inner border transition-all ${estado === true ? 'bg-emerald-100 text-emerald-800 border-emerald-300 focus:border-emerald-500' : estado === false ? 'bg-rose-100 text-rose-800 border-rose-400 focus:border-rose-500' : 'bg-white text-slate-800 border-slate-300 focus:border-cyan-400'}`} 
        />
        <span className="absolute right-4 top-2.5 text-xs font-black text-slate-400">°C</span>
      </div>
      
      {estado === false && <p className="text-[10px] text-rose-600 font-bold mt-2 leading-tight">⚠️ Temperatura fuera del rango permitido.</p>}
    </div>
  );
};

// ==============================================================
// COMPONENTE PRINCIPAL
// ==============================================================
export default function FormatoControlTemperaturas({ onBack }) {
  const [datos, setDatos] = useState({
    fechaEvento: new Date().toISOString().split('T')[0],
    turno: "Mañana (8:00 AM)",
    pdv: "",
    verificador: "",
    observaciones: ""
  });

  const [temperaturas, setTemperaturas] = useState({
    ref_lacteos: "",
    ref_pesca: "",
    ref_carnes: "",
    ref_preparados: "",
    ref_bebidas: "",
    con_pesca: "",
    con_carnes: "",
    cal_preparados: ""
  });

  const [listaSedes, setListaSedes] = useState([]);
  const [customAlert, setCustomAlert] = useState({ show: false, message: "", type: "success" });
  const [empleadosDB, setEmpleadosDB] = useState([]);
  const [cargandoEmpleados, setCargandoEmpleados] = useState(false);
  const [buscarTodasSedes, setBuscarTodasSedes] = useState(false);

  useEffect(() => {
    const cargarDatosMaestros = async () => {
      setCargandoEmpleados(true);
      try {
        const token = localStorage.getItem("token");
        const API_URL = process.env.REACT_APP_API_URL;
        
        // Carga de sedes dinámicas desde MySQL
        const resSedes = await fetch(`${process.env.REACT_APP_API_URL}/api/sedes`);
        const dataSedes = await resSedes.json();
        if (dataSedes.success && Array.isArray(dataSedes.sedes)) {
          setListaSedes(dataSedes.sedes);
        }

        // Carga de empleados
        const resEmp = await fetch(`${API_URL}/api/empleados`, {
          headers: { ...(token && { Authorization: `Bearer ${token}` }) }
        });
        const dataEmp = await resEmp.json();
        setEmpleadosDB(Array.isArray(dataEmp) ? dataEmp : (dataEmp.data || []));
      } catch (error) {
        console.error("Error cargando datos maestros:", error);
      } finally {
        setCargandoEmpleados(false);
      }
    };
    cargarDatosMaestros();
  }, []);

  const listaParaMostrar = empleadosDB.filter(emp => {
    const estaActivo = !emp.empl_fecret || String(emp.empl_fecret).toUpperCase() === 'NULL' || emp.empl_fecret === '0000-00-00';
    if (!estaActivo) return false;
    if (buscarTodasSedes) return true;
    if (!emp.area_nombre || !datos.pdv) return false;
    return emp.area_nombre.toUpperCase() === datos.pdv.toUpperCase();
  });

  const handleChangeDatos = (e) => {
    const { name, value } = e.target;
    setDatos(prev => ({ ...prev, [name]: value, ...(name === 'pdv' && { verificador: "" }) }));
  };

  const handleChangeTemp = (e) => {
    const { name, value } = e.target;
    setTemperaturas(prev => ({ ...prev, [name]: value }));
  };

  const evaluarTemp = (key, val) => {
    if (val === "") return null; 
    const num = parseFloat(val);
    if (isNaN(num)) return null;

    if (key === "ref_lacteos") return num >= 2 && num <= 6;
    if (key === "ref_pesca") return num >= 0 && num <= 4;
    if (key === "ref_carnes") return num >= 0 && num <= 6; 
    if (key === "ref_preparados") return num >= 2 && num <= 6;
    if (key === "ref_bebidas") return true; 
    
    if (key === "con_pesca" || key === "con_carnes") return num <= -18;
    
    if (key === "cal_preparados") return num >= 60;
    
    return true;
  };

  const handleSubmit = async () => {
    if (!datos.pdv || !datos.verificador) {
      return setCustomAlert({ show: true, message: "La Sede y el Verificador son obligatorios.", type: "warning" });
    }

    const hayLecturas = Object.values(temperaturas).some(val => val !== "");
    if (!hayLecturas) {
      return setCustomAlert({ show: true, message: "Debes registrar al menos una temperatura.", type: "warning" });
    }

    const hayFallos = Object.entries(temperaturas).some(([key, val]) => evaluarTemp(key, val) === false);
    if (hayFallos && !datos.observaciones.trim()) {
      return setCustomAlert({ show: true, message: "Hay equipos fuera del rango permitido. Debes registrar la Acción Correctiva en las Observaciones.", type: "warning" });
    }

    try {
      const token = localStorage.getItem("token");
      const API_URL = process.env.REACT_APP_API_URL;

      const payload = {
        tipo_formulario: "CONTROL_TEMPERATURAS",
        datosGenerales: { pdv: datos.pdv, fecha: datos.fechaEvento, evaluador: datos.verificador },
        respuestas: { turno: datos.turno, ...temperaturas, observaciones: datos.observaciones },
        puntaje_global: hayFallos ? 0 : 100
      };

      const res = await fetch(`${API_URL}/api/formularios/guardar`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token && { Authorization: `Bearer ${token}` }) },
        body: JSON.stringify(payload)
      });

      const dataRes = await res.json();
      if (dataRes.success) {
        setCustomAlert({ show: true, message: "Temperaturas guardadas exitosamente.", type: "success" });
        setTimeout(() => {
          setTemperaturas({ 
            ref_lacteos: "", ref_pesca: "", ref_carnes: "", ref_preparados: "", ref_bebidas: "", 
            con_pesca: "", con_carnes: "", cal_preparados: "" 
          });
          setDatos(prev => ({ ...prev, observaciones: "" }));
          setCustomAlert({ ...customAlert, show: false });
        }, 1200);
      } else {
        setCustomAlert({ show: true, message: dataRes.message || "Error al guardar.", type: "error" });
      }
    } catch (error) {
      setCustomAlert({ show: true, message: "Error de conexión.", type: "error" });
    }
  };

  return (
    <div className="bg-white/20 backdrop-blur-3xl rounded-2xl sm:rounded-[34px] shadow-2xl max-w-5xl w-full mx-auto border border-white/30 animate-fade-in relative h-[90vh] overflow-hidden flex flex-col">
      <div className="bg-gradient-to-r from-cyan-600/90 to-blue-800/90 border-b border-white/20 px-6 py-4 z-40 flex items-center shadow-md shrink-0">
        <button onClick={onBack} className="mr-4 w-10 h-10 flex-none bg-white/20 hover:bg-white/40 text-white flex items-center justify-center rounded-full transition-all border border-white/40 shadow-sm cursor-pointer">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"></path></svg>
        </button>
        <div>
          <h1 className="text-lg sm:text-xl font-black text-white leading-tight">Control de Temperaturas</h1>
          <span className="bg-white/20 text-white border border-white/30 text-[10px] font-bold px-2 py-0.5 rounded-md mt-1 inline-block">CÓDIGO: FT-CD-02 | Versión: 03</span>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 sm:p-8 space-y-6 custom-scrollbar">
        <div className="bg-white/60 backdrop-blur-xl p-6 rounded-[24px] shadow-sm border border-white/80">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase tracking-wide">Fecha *</label>
              <input 
                type="date" 
                name="fechaEvento" 
                value={datos.fechaEvento} 
                onChange={handleChangeDatos} 
                className="w-full h-[40px] bg-white/80 border-transparent focus:border-cyan-400 rounded-xl px-4 text-xs font-bold text-slate-800 shadow-sm text-center block" 
                style={{ margin: 0, padding: '0 1rem', lineHeight: '40px' }} 
              />
            </div>
            
            {/* SEDE DINÁMICA DESDE LA BASE DE DATOS */}
            <div>
              <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase tracking-wide">Sede *</label>
              <select name="pdv" value={datos.pdv} onChange={handleChangeDatos} className="w-full h-[40px] bg-white/80 border-transparent focus:border-cyan-400 rounded-xl px-4 text-xs font-bold text-slate-800 shadow-sm cursor-pointer text-center" style={{ margin: 0 }}>
                <option value="" disabled>-- Seleccione una sede --</option>
                {listaSedes.map(sede => (
                  <option key={sede.id} value={sede.nombre.toUpperCase()}>
                    {sede.nombre} {sede.codigo_bodega ? `(Bodega: ${sede.codigo_bodega})` : ""}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase tracking-wide">Turno *</label>
              <select name="turno" value={datos.turno} onChange={handleChangeDatos} className="w-full h-[40px] bg-white/80 border-transparent focus:border-cyan-400 rounded-xl px-4 text-xs font-bold text-slate-800 shadow-sm cursor-pointer text-center" style={{ margin: 0 }}>
                {TURNOS.map(turno => <option key={turno} value={turno}>{turno}</option>)}
              </select>
            </div>
          </div>
          <div className="border-t border-slate-200/60 pt-4 relative">
            <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase">Responsable (Verificador) *</label>
            <input type="text" name="verificador" list="listaVerificadores" value={datos.verificador} onChange={handleChangeDatos} placeholder={cargandoEmpleados ? "Cargando BD..." : datos.pdv ? "Escribe o elige nombre..." : "Elige sede primero"} disabled={!datos.pdv || cargandoEmpleados} className="w-full h-[40px] bg-white/80 border-transparent focus:border-cyan-400 rounded-xl px-3 text-xs font-bold text-slate-800 shadow-sm disabled:opacity-50" autoComplete="off" />
            <datalist id="listaVerificadores">
              {listaParaMostrar.map((emp, i) => <option key={i} value={emp.terc_nom}>{buscarTodasSedes ? `${emp.empl_cargo || "Sin Cargo"} (${emp.area_nombre || "Sede"})` : (emp.empl_cargo || "Sin Cargo")}</option>)}
            </datalist>
            <div className="mt-2 flex items-center justify-end">
              <input type="checkbox" id="buscarGlobalV" checked={buscarTodasSedes} onChange={(e) => setBuscarTodasSedes(e.target.checked)} className="mr-1.5 cursor-pointer accent-cyan-600" disabled={!datos.pdv} />
              <label htmlFor="buscarGlobalV" className="text-[9px] text-cyan-700 font-black cursor-pointer uppercase">Buscar en todas las sedes</label>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white/60 backdrop-blur-xl p-6 rounded-[24px] border border-cyan-200 shadow-sm">
            <h3 className="text-sm font-black text-cyan-800 uppercase tracking-widest mb-4 flex items-center gap-2 border-b border-cyan-200 pb-2"><span>❄️</span> REFRIGERACIÓN</h3>
            <div className="space-y-4">
              <TarjetaEquipo id="ref_lacteos" titulo="Lácteos Refrigerados" icono="🥛" rangoTexto="Rango: 2°C a 6°C (Ideal: 2-4°C)" valor={temperaturas.ref_lacteos} onChange={handleChangeTemp} evaluarTemp={evaluarTemp} />
              <TarjetaEquipo id="ref_pesca" titulo="Pescados / Mariscos" icono="🐟" rangoTexto="Rango: 0°C a 4°C" valor={temperaturas.ref_pesca} onChange={handleChangeTemp} evaluarTemp={evaluarTemp} />
              <TarjetaEquipo id="ref_carnes" titulo="Carnes Refrigeradas" icono="🥩" rangoTexto="Según normatividad / Rotulado" valor={temperaturas.ref_carnes} onChange={handleChangeTemp} evaluarTemp={evaluarTemp} />
              <TarjetaEquipo id="ref_preparados" titulo="Alimentos Preparados" icono="🍱" rangoTexto="Rango: 2°C a 6°C" valor={temperaturas.ref_preparados} onChange={handleChangeTemp} evaluarTemp={evaluarTemp} />
              <TarjetaEquipo id="ref_bebidas" titulo="Bebidas" icono="🥤" rangoTexto="Según fabricante" valor={temperaturas.ref_bebidas} onChange={handleChangeTemp} evaluarTemp={evaluarTemp} />
            </div>
          </div>
          <div className="space-y-6">
            <div className="bg-white/60 backdrop-blur-xl p-6 rounded-[24px] border border-blue-200 shadow-sm">
              <h3 className="text-sm font-black text-blue-800 uppercase tracking-widest mb-4 flex items-center gap-2 border-b border-blue-200 pb-2"><span>🧊</span> CONGELACIÓN</h3>
              <div className="space-y-4">
                <TarjetaEquipo id="con_pesca" titulo="Pescados / Mariscos" icono="🐟" rangoTexto="Rango: -18°C o inferior" valor={temperaturas.con_pesca} onChange={handleChangeTemp} evaluarTemp={evaluarTemp} />
                <TarjetaEquipo id="con_carnes" titulo="Carnes Congeladas" icono="🥩" rangoTexto="Rango: -18°C o inferior" valor={temperaturas.con_carnes} onChange={handleChangeTemp} evaluarTemp={evaluarTemp} />
              </div>
            </div>
            <div className="bg-white/60 backdrop-blur-xl p-6 rounded-[24px] border border-orange-200 shadow-sm">
              <h3 className="text-sm font-black text-orange-800 uppercase tracking-widest mb-4 flex items-center gap-2 border-b border-orange-200 pb-2"><span>🔥</span> MANTENEDOR CALIENTE</h3>
              <div className="space-y-4">
                <TarjetaEquipo id="cal_preparados" titulo="Alimentos Calientes" icono="♨️" rangoTexto="Rango: Mayor a 60°C" valor={temperaturas.cal_preparados} onChange={handleChangeTemp} evaluarTemp={evaluarTemp} />
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white/60 backdrop-blur-xl p-6 rounded-[24px] shadow-sm border border-white/80">
          <label className="block text-[10px] font-black text-slate-700 mb-2 uppercase tracking-widest">Observaciones y Acciones Correctivas (Requerido si hay fallos)</label>
          <textarea name="observaciones" value={datos.observaciones} onChange={handleChangeDatos} placeholder="Describe medidas aquí si es necesario..." className="w-full h-[80px] bg-white/90 border border-slate-200 focus:border-cyan-400 rounded-xl px-4 py-3 text-xs font-medium text-slate-800 shadow-sm resize-none" />
          <button onClick={handleSubmit} className="w-full mt-6 bg-gradient-to-r from-cyan-600 to-blue-700 hover:from-cyan-500 hover:to-blue-600 text-white font-black py-4 rounded-xl shadow-lg active:scale-95 transition-all text-[11px] tracking-widest uppercase cursor-pointer border border-cyan-400">Guardar Lecturas</button>
        </div>
      </div>

      {customAlert.show && (
        <SafePortalModal>
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-md flex justify-center items-center p-4 z-[99999] animate-fade-in">
            <div className="bg-white/50 backdrop-blur-3xl rounded-[32px] p-6 sm:p-8 max-w-sm w-full text-center shadow-[0_30px_90px_rgba(0,0,0,0.2)] border border-white/80">
              <div className={`w-20 h-20 mx-auto rounded-3xl flex items-center justify-center mb-5 shadow-sm border border-white/80 ${customAlert.type === 'success' ? 'bg-emerald-500/20 text-emerald-700' : customAlert.type === 'warning' ? 'bg-amber-500/20 text-amber-700' : 'bg-red-500/20 text-red-700'}`}>
                <span className="text-4xl">{customAlert.type === 'success' ? '✅' : customAlert.type === 'warning' ? '⚠️' : '❌'}</span>
              </div>
              <h3 className="text-xl font-extrabold text-slate-800 mb-2.5">{customAlert.type === 'success' ? '¡Éxito!' : 'Atención'}</h3>
              <p className="text-sm text-slate-700 font-medium mb-8 leading-relaxed px-2">{customAlert.message}</p>
              <button onClick={() => setCustomAlert({ ...customAlert, show: false })} className={`w-full py-3.5 rounded-2xl font-bold text-white transition-all shadow-lg cursor-pointer ${customAlert.type === 'success' ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-amber-500 hover:bg-amber-400'}`}>Entendido</button>
            </div>
          </div>
        </SafePortalModal>
      )}
    </div>
  );
}